/**
 * Minimal hash-based client-side router.
 *
 * Usage:
 *   router.on('/add-asset', renderAddAsset);
 *   router.on('/checkout', renderCheckout);
 *   router.start();
 *   router.navigate('/add-asset');
 */

class Router {
  constructor() {
    this.routes = new Map();
    this.currentCleanup = null;
  }

  /**
   * Register a route.
   * @param {string} path — e.g. '/add-asset'
   * @param {(params: object) => void|Function} handler — render function.
   *   May return a cleanup function that is called before navigating away.
   */
  on(path, handler) {
    this.routes.set(path, handler);
    return this;
  }

  /**
   * Navigate to a route.
   */
  navigate(path) {
    window.location.hash = '#' + path;
  }

  /**
   * Get the current route path.
   */
  current() {
    return window.location.hash.slice(1) || '/';
  }

  /**
   * Start listening for hash changes.
   */
  start() {
    const handle = () => this._resolve();
    window.addEventListener('hashchange', handle);
    this._resolve();
  }

  _resolve() {
    const path = this.current();

    // Clean up previous route
    if (typeof this.currentCleanup === 'function') {
      this.currentCleanup();
      this.currentCleanup = null;
    }

    // Try exact match first
    if (this.routes.has(path)) {
      this.currentCleanup = this.routes.get(path)({}) || null;
      return;
    }

    // Try pattern match (e.g. /edit-asset/:id)
    for (const [pattern, handler] of this.routes) {
      const params = matchRoute(pattern, path);
      if (params) {
        this.currentCleanup = handler(params) || null;
        return;
      }
    }

    // Fallback to home
    if (this.routes.has('/')) {
      this.currentCleanup = this.routes.get('/')({}) || null;
    }
  }
}

/**
 * Match a route pattern like '/edit-asset/:id' against a path like '/edit-asset/42'.
 * Returns params object or null.
 */
function matchRoute(pattern, path) {
  const patternParts = pattern.split('/');
  const pathParts = path.split('/');

  if (patternParts.length !== pathParts.length) return null;

  const params = {};
  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(':')) {
      params[patternParts[i].slice(1)] = decodeURIComponent(pathParts[i]);
    } else if (patternParts[i] !== pathParts[i]) {
      return null;
    }
  }
  return params;
}

export const router = new Router();
